import { Button, Modal } from '@clearcover/atlas';
import {
  ApplyPolicyDraftPayloadType,
  ApplyPolicyDraftResponseType,
  GetChangeSummaryResponseType,
  GetPolicyDraftResponseType,
  PolicyDiff,
  PolicyDocumentType,
  PolicyUpdatedActivity,
  PremiumChangesType,
} from '@clearcover/core-app-types';
import { StaggeredAnimatedComponent } from 'Policies/components';
import { PacketSignatureSection } from 'Policies/components/PacketSignatureSection';
import {
  removeCurrentPacketFromStorage,
  removeCurrentSignatureRequestIdFromStorage,
} from 'Policies/components/PacketSignatureSection/utils';
import { PolicyDetailsHeader } from 'Policies/components/PolicyDetailsHeader';
import { PolicyDraftSummaryHeaderBar } from 'Policies/components/PolicyDraftHeaderBar';
import { CoverageSection } from 'Policies/components/PolicySummary';
import { PaymentAndChangesSection } from 'Policies/components/PolicySummary/PaymentAndChangesSection';
import { UnderwritingEndorsementConsent } from 'Policies/components/PolicySummary/UnderwritingEndorsementConsent';
import { PolicyContext } from 'Policies/utils';
import {
  Column,
  ConfirmationModal,
  LoadingPage,
  useToast,
} from 'Shared/components';
import { useResourceEffectiveGrants } from 'Shared/hooks';
import { useApi, useLazyApi, useNewApi } from 'Shared/services/Api';
import { combineStrings, currency, formatMoney } from 'Shared/utils';
import { default as classNames, default as classnames } from 'classnames';
import { Formik, FormikHelpers } from 'formik';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { RouteComponentProps, StaticContext } from 'react-router';
import * as Yup from 'yup';
import { useAuth0 } from '../../../Auth';
import {
  PolicySignaturesRequired,
  SignatureRequiredFormResponseType,
} from '../../components/PolicySignaturesRequired';

import { WarningModal } from './WarningModal';

import { ChangeSummarySection } from 'Policies/components/PolicySummary/ChangeSummarySection/ChangeSummarySection';
import styles from './styles.module.css';

export type PolicySummaryPaymentFormValues = {
  paymentMethodId: string;
  paymentAmount: number;
};

const policyEndorsementSchema = Yup.object({
  paymentAmount: Yup.number().required('Required'),
  paymentMethodId: Yup.string(),
});

const isNewBusinessSignatureWorkflow = (workflow: string | undefined) =>
  workflow ? ['NEW_BUSINESS', 'NEW_BUSINESS_AGENT'].includes(workflow) : false;

const TotalRatedPremium: FC<{ premiumChanges: PremiumChangesType }> = ({
  premiumChanges,
}) => {
  const totalRatedPremium = formatMoney(
    premiumChanges.newFullTermWrittenPremium
  );
  const totalPremiumChange = currency(
    premiumChanges.newFullTermWrittenPremium
  ).subtract(premiumChanges.previousFullTermWrittenPremium).value;

  const absoluteRatedPremiumChange = currency(Math.abs(totalPremiumChange));
  const percentRatedPremiumChange = absoluteRatedPremiumChange
    .divide(premiumChanges.previousFullTermWrittenPremium)
    .multiply(100).value;
  const totalRatedPremiumDisplay = `${totalRatedPremium} (${
    totalPremiumChange < 0 ? '-' : '+'
  }${absoluteRatedPremiumChange.format(true)}/${percentRatedPremiumChange}%)`;

  return (
    <div className={styles.totalRatedPremium}>
      <span className={styles.label}>Total Rated Premium:</span>
      <span>{totalRatedPremiumDisplay}</span>
    </div>
  );
};

export const PolicyDraftSummary: FC<RouteComponentProps<
  {
    id: string;
    draftId: string;
  },
  StaticContext,
  {
    addVehicle?: boolean;
    effectiveDate?: string;
    updatePolicySuccess?: boolean;
  }
>> = ({
  match: {
    params: { id: policyId, draftId },
  },
  history,
}) => {
  const [
    showRenewalErrorWarningModal,
    setShowRenewalErrorWarningModal,
  ] = useState(false);
  const [
    showRenewalChangesWarningModal,
    setShowRenewalChangesWarningModal,
  ] = useState(false);

  const [accessConsentChecked, setAccessConsentChecked] = useState(false);

  const formDataRef = useRef<ApplyPolicyDraftPayloadType>(null);
  const setSubmittingRef = useRef<(isSubmitting: boolean) => void>(() => {});
  const effectiveDateRef = useRef<string | undefined>(undefined);

  const { toast } = useToast();
  const { user } = useAuth0();
  const [
    {
      data: policySummaryResponse,
      loading: policySummaryLoading,
      error: policySummaryError,
    },
  ] = useApi<GetChangeSummaryResponseType>(
    `/policies/${policyId}/draft/${draftId}/summary`
  );

  const [
    { data: signatureRequiredForms, loading: loadingSignatureRequiredForms },
  ] = useApi<SignatureRequiredFormResponseType>(
    `/policies/${policyId}/drafts/${draftId}/signableForms/update`,
    false
  );

  const [{ data: documentsData, loading: loadingDocumentsData }] = useNewApi<{
    documents: Array<PolicyDocumentType>;
  }>(
    {
      url: `/policies/${policyId}/documentsList`,
    },
    false,
    false
  );

  const [
    { data: draft, loading: loadingPolicyDraft, refetch: refetchDraft },
  ] = useApi<GetPolicyDraftResponseType>(
    `/policies/${policyId}/drafts/${draftId}`,
    false
  );
  const [{ data: draftDiff, loading: loadingDraftDiff }] = useApi<{
    diff: PolicyDiff;
  }>(`/policies/${policyId}/drafts/${draftId}/diff`, false);
  const {
    loading: resourceEffectiveGrantsLoading,
    resourceEffectiveGrants,
  } = useResourceEffectiveGrants(draft?.policyDraft.policyDetails.policyId);

  const [
    { loading: terminateDraftloading, error: terminateDraftError },
    executeTerminateDraft,
  ] = useLazyApi({
    url: `policies/${policyId}/drafts/${draftId}/terminate`,
    method: 'POST',
  });

  const applyDraftUrl = useMemo(
    () =>
      draft?.policyDraft.draftType === 'renewal_quote'
        ? `/policies/${policyId}/applyRenewalQuote`
        : `/policies/${policyId}/draft/${draftId}/apply`,
    [draft?.policyDraft.draftType, policyId, draftId]
  );
  const [, applyPolicyChange] = useLazyApi<ApplyPolicyDraftResponseType>({
    method: 'POST',
    url: applyDraftUrl,
  });

  const hasUnsignedNewBusinessSignaturePacket =
    documentsData?.documents.some(
      doc =>
        isNewBusinessSignatureWorkflow(doc.signatureWorkflow) &&
        !doc.completedAt
    ) ?? false;

  const displaySignatureDocuments =
    signatureRequiredForms &&
    signatureRequiredForms.forms.length > 0 &&
    (policySummaryResponse?.policyInfo.termInfo.termNumber > 1 ||
      !hasUnsignedNewBusinessSignaturePacket);

  const [packetsHaveBeenSigned, setPacketsSigned] = useState(false);

  useEffect(() => {
    setPacketsSigned(
      displaySignatureDocuments && signatureRequiredForms?.useNewSignatureUI
        ? false
        : true
    );
  }, [signatureRequiredForms, displaySignatureDocuments]);

  const renewalDraftId = draft?.policyDraft.draftList.find(
    d => d.type === 'renewal'
  )?.id;

  const redirectToPolicy = useCallback(
    (state?: any) => {
      if (draft.policyDraft.draftType === 'renewal_quote') {
        // Note: Although `renewalDraftId` is nullable, it will always have a value
        // for renewal quotes, as they are built off of an existing renewal drafts.
        // If the renewal draft is terminated or applied, the renewal quote will be
        // discarded automatically.
        history.push(`/policies/${policyId}/draft/${renewalDraftId}`, state);
      } else {
        history.push(`/policies/${policyId}`, state);
      }
    },
    [draft?.policyDraft.draftType, history, policyId, renewalDraftId]
  );

  if (
    policySummaryLoading ||
    loadingSignatureRequiredForms ||
    loadingPolicyDraft ||
    loadingDocumentsData ||
    resourceEffectiveGrantsLoading ||
    loadingDraftDiff
  )
    return <LoadingPage />;
  if (policySummaryError) return <div>{policySummaryError.message}</div>;
  if (!policySummaryResponse)
    return <h1>No data found for the policy draft by id #{draftId}</h1>;

  const {
    changesSummary,
    coverages,
    policyInfo,
    paymentInfo,
    paymentFeeInfo,
    premiumChanges,
    endorsementConsents,
  } = policySummaryResponse;

  const hasAddedDriversChanges =
    changesSummary.drivers.length > 0 &&
    changesSummary.drivers.some(({ message }) => message.includes('added'));
  const hasAddedVehiclesChanges =
    changesSummary.vehicles.length > 0 &&
    changesSummary.vehicles.some(({ message }) => message.includes('added'));

  const policyDraft = draft.policyDraft;

  const requireAccessConsent =
    policyDraft.requiredAccessStatements &&
    (hasAddedDriversChanges || hasAddedVehiclesChanges);

  const getUnderwritingConsentMarkdown = () => {
    const markdowns: string[] = [];

    if (hasAddedDriversChanges) {
      const markdown = endorsementConsents?.find(
        consent => consent.flow === 'endorsement-driver'
      )?.markdown;
      if (markdown) markdowns.push(markdown);
    }
    if (hasAddedVehiclesChanges) {
      const markdown = endorsementConsents?.find(
        consent => consent.flow === 'endorsement-vehicle'
      )?.markdown;
      if (markdown) markdowns.push(markdown);
    }

    return markdowns.join('\n\n');
  };

  const paymentMethods = paymentInfo.hasRefund
    ? [paymentInfo.defaultPaymentMethod]
    : paymentInfo.paymentMethods;
  const hasProcessOnePaymentMethod = paymentMethods.some(
    method => method.paymentProcessor === 'PROCESS_ONE'
  );

  const INITIAL_FORM_VALUES: PolicySummaryPaymentFormValues = {
    paymentMethodId: paymentInfo.defaultPaymentMethod.id,
    paymentAmount: policySummaryResponse.paymentInfo.isFullPay
      ? paymentFeeInfo.paymentFeeEnabledState
        ? currency(
            premiumChanges.amountDue[paymentInfo.defaultPaymentMethod.type]
          ).value
        : currency(premiumChanges.amountDue.raw).value
      : 0,
  };

  const proceedWithRedirect = () => {
    const redirectData: {
      updatePolicySuccess: boolean;
      effectiveDate?: string;
    } = {
      updatePolicySuccess: true,
    };

    if (effectiveDateRef.current) {
      redirectData.effectiveDate = effectiveDateRef.current;
    }

    redirectToPolicy(redirectData);
  };

  const handleMessagesAndRedirect = (
    messages:
      | Array<{ type: string; message: string; description: object }>
      | undefined
  ) => {
    const renewalError = messages
      ? messages.find(
          msg => msg.type === 'RenewalDraftService.potentiallyUpdateRenewal'
        )
      : undefined;

    if (renewalError) {
      setShowRenewalErrorWarningModal(true);
    } else {
      proceedWithRedirect();
    }
  };

  const createConsentPayload = (
    consent: any,
    items: Array<{ identifier: string }>,
    itemType: 'driver' | 'vehicle'
  ) => ({
    tags: {
      flow: consent.flow,
      type: consent.type,
      actor: consent.actor,
    },
    appliesTo: items.map(item => ({
      id: item.identifier,
      type: itemType,
    })),
    identifier: consent.identifier,
    collectedAt: new Date().toISOString(),
    collectedBy: user?.email,
  });

  const collectConsentsIfNeeded = (data: ApplyPolicyDraftPayloadType) => {
    if (!requireAccessConsent || !accessConsentChecked) {
      return;
    }

    const collectedConsents = [];

    if (hasAddedDriversChanges) {
      const driverConsent = endorsementConsents?.find(
        (consent: any) => consent.flow === 'endorsement-driver'
      );
      if (driverConsent) {
        collectedConsents.push(
          createConsentPayload(driverConsent, changesSummary.drivers, 'driver')
        );
      }
    }

    if (hasAddedVehiclesChanges) {
      const vehicleConsent = endorsementConsents?.find(
        (consent: any) => consent.flow === 'endorsement-vehicle'
      );
      if (vehicleConsent) {
        collectedConsents.push(
          createConsentPayload(
            vehicleConsent,
            changesSummary.vehicles,
            'vehicle'
          )
        );
      }
    }

    if (collectedConsents.length > 0) {
      data.collectedConsents = collectedConsents;
    }
  };

  const cleanupSignatureStorage = () => {
    if (displaySignatureDocuments) {
      removeCurrentSignatureRequestIdFromStorage(policyId, draftId);
      removeCurrentPacketFromStorage(policyId, draftId);
    }
  };

  const handleApplySuccess = (effectiveDate: string, messages: any) => {
    effectiveDateRef.current = effectiveDate;
    cleanupSignatureStorage();
    handleMessagesAndRedirect(messages);
  };

  const handleApplyError = (error: any) => {
    setSubmittingRef.current(false);
    toast({
      message: error.response?.data?.message ?? error.message,
      type: 'error',
      position: 'top',
      withSecondaryNav: false,
      width: 'full',
      icon: 'info',
    });
  };

  const applyChanges = async (data: ApplyPolicyDraftPayloadType) => {
    collectConsentsIfNeeded(data);

    applyPolicyChange({ data })
      .then(({ data: { effectiveDate, messages } }) =>
        handleApplySuccess(effectiveDate, messages)
      )
      .catch(handleApplyError);
  };

  const submitPolicyChanges: (
    values: PolicySummaryPaymentFormValues,
    { setSubmitting }: FormikHelpers<PolicySummaryPaymentFormValues>
  ) => void = (values, { setSubmitting }) => {
    if (requireAccessConsent && !accessConsentChecked) {
      return;
    }

    const data: ApplyPolicyDraftPayloadType = paymentInfo.hasRefund
      ? { paymentAmount: 0 }
      : {
          paymentMethodId: values.paymentMethodId,
          paymentAmount: values.paymentAmount,
        };

    setSubmittingRef.current = setSubmitting;

    if (renewalDraftId && draftType === 'mid_term') {
      setShowRenewalChangesWarningModal(true);
      formDataRef.current = data;
    } else {
      applyChanges(data);
    }
  };

  const confirmAndSubmit = () => {
    setShowRenewalChangesWarningModal(false);
    if (formDataRef.current) {
      applyChanges(formDataRef.current);
    }
  };

  const policyDetails = policyDraft.policyDetails;
  const applicantName = combineStrings([
    policyDraft.policyHolder.name.firstName,
    policyDraft.policyHolder.name.lastName,
  ]);
  const draftType = policyDraft.draftType;

  const underwritingConsentMarkdown = getUnderwritingConsentMarkdown();

  const syntheticDiffActivity = {
    data: { changeset: draftDiff.diff },
  } as PolicyUpdatedActivity;

  return (
    <PolicyContext.Provider
      value={{
        details: {
          region: policyDetails.policyRegion || '',
          effectiveDate: policyDetails.effectivePeriod.startsAt || '',
          timeZone: policyDetails.policyTimeZone || 'America/Chicago',
          filingIdentifier: policyDetails.filingIdentifier || '',
          configDiscoveryParams: policyDetails.configDiscoveryParams || '',
          isPolicyDraft: true,
          isRenewalDraft: draftType === 'renewal',
          draftType,
          termId: policyDetails.termInfo.termId || '',
          mailingState: policyDetails.addresses.mailing.state || '',
          showDriverCfrs: policyDetails.showDriverCfrs || false,
          askForUserReportedLicenseStatus:
            policyDetails.askForUserReportedLicenseStatus || false,
          requiredAccessStatements: policyDraft.requiredAccessStatements,
          showMilitarySection: policyDetails.showMilitarySection ?? true,
          showStudentSection: policyDetails.showStudentSection ?? true,
          showEducation: policyDetails.showEducation ?? true,
          showOccupation: policyDetails.showOccupation ?? true,
          showRegisteredToApplicantOrSpouse:
            policyDetails.showRegisteredToApplicantOrSpouse ?? true,
          showExcludedDriverGender:
            policyDetails.showExcludedDriverGender ?? false,
          showExcludedDriverMaritalStatus:
            policyDetails.showExcludedDriverMaritalStatus ?? false,
          showPricingData: policyDetails.showPricingData ?? true,
        },
        person: {
          name: applicantName,
        },
        flags: {
          renewalQuotesEnabled: false, // unused in this context
          mandatedDownPay: draft.policyDraft.mandatedDownPay ?? false,
          isFailingUnderwriting:
            draft.policyDraft.isFailingUnderwriting ?? false,
        },
        canEndorseDuringRegionalEvents: false, // unused in this context
        isEndorseablePolicy: false, // unused in this context
        systemOfRecord: draft.policyDraft.systemOfRecord,
        resourceEffectiveGrants,
        refetchHandler: refetchDraft,
        loading: loadingPolicyDraft,
        productSegment: policyDetails.productSegment || 'standardAuto',
        draftList: draft.policyDraft.draftList,
        removeDraft: () => {},
      }}
    >
      <Formik
        validationSchema={policyEndorsementSchema}
        initialValues={INITIAL_FORM_VALUES}
        onSubmit={submitPolicyChanges}
        validateOnChange
        validateOnMount
      >
        {({ handleSubmit, isSubmitting, isValid }) => (
          <div className={styles.summaryContainer}>
            <PolicyDraftSummaryHeaderBar
              policyId={policyId}
              renewalDraftId={renewalDraftId}
              draftType={draftType}
              updatedAt={draft.policyDraft.updatedAt}
              timeZone={policyDetails.policyTimeZone}
            />
            <StaggeredAnimatedComponent>
              <PolicyDetailsHeader
                draftList={[]}
                termStatusDetailsList={
                  policyInfo.termInfo.termStatusDetailsList
                }
                applicantName={policyInfo.applicantName}
                effectivePeriod={policyInfo.effectivePeriod}
                policyNumber={policyInfo.policyNumber}
                termInfo={policyInfo.termInfo}
                processOne={hasProcessOnePaymentMethod}
                productSegment={
                  draft?.policyDraft?.policyDetails?.productSegment
                }
                renewalDraftId={renewalDraftId}
                ignoreBanners={['pending_renewal']}
              />
            </StaggeredAnimatedComponent>
            <Column className={styles.summaryCol}>
              <StaggeredAnimatedComponent>
                <Column className={styles.summarySection}>
                  <div
                    className={classnames(styles.summaryHeader, styles.review)}
                  >
                    Review Policy Changes
                  </div>
                </Column>
              </StaggeredAnimatedComponent>
              <StaggeredAnimatedComponent className={styles.expansionContainer}>
                <Column className={styles.summarySection}>
                  <div className={classNames(styles.summaryHeader)}>
                    Change Summary
                  </div>
                  <ChangeSummarySection
                    activity={syntheticDiffActivity}
                    className={classNames(styles.changeSummary)}
                  />
                </Column>
              </StaggeredAnimatedComponent>
              <StaggeredAnimatedComponent>
                <Column className={styles.summarySection}>
                  <div className={styles.summaryHeader}>Rated Premium</div>
                  <CoverageSection coverages={coverages} />
                  <TotalRatedPremium premiumChanges={premiumChanges} />
                </Column>
              </StaggeredAnimatedComponent>
              <StaggeredAnimatedComponent>
                <Column className={classNames(styles.paymentSection)}>
                  <PaymentAndChangesSection
                    policySummaryResponse={policySummaryResponse}
                    draftType={draftType}
                  />
                </Column>
              </StaggeredAnimatedComponent>
              {underwritingConsentMarkdown && (
                <StaggeredAnimatedComponent>
                  <Column
                    className={classNames(
                      styles.summarySection,
                      styles.endorsementsAcceptance
                    )}
                  >
                    <UnderwritingEndorsementConsent
                      markdown={underwritingConsentMarkdown}
                      accessConsentChecked={accessConsentChecked}
                      setAccessConsentChecked={setAccessConsentChecked}
                    />
                  </Column>
                </StaggeredAnimatedComponent>
              )}
              <StaggeredAnimatedComponent>
                {displaySignatureDocuments && (
                  <Column className={styles.summarySection}>
                    <div className={styles.summaryHeader}>
                      eSignature Required
                    </div>
                    {signatureRequiredForms.useNewSignatureUI ? (
                      <PacketSignatureSection
                        forms={signatureRequiredForms.forms}
                        policyId={policyId}
                        draftId={draftId}
                        canViewDocuments={user?.partner?.name === 'clearcover'}
                        policyHolder={{
                          email:
                            draft?.policyDraft?.policyHolder?.contactInfo
                              ?.email,
                          name: combineStrings([
                            draft?.policyDraft.policyHolder.name.firstName,
                            draft?.policyDraft.policyHolder.name.lastName,
                          ]),
                        }}
                        timeZone={
                          draft?.policyDraft?.policyDetails.policyTimeZone
                        }
                        setPacketsHaveBeenSigned={() => setPacketsSigned(true)}
                      />
                    ) : (
                      <PolicySignaturesRequired
                        forms={signatureRequiredForms.forms}
                        policyHolderEmail={
                          draft?.policyDraft?.policyHolder?.contactInfo?.email
                        }
                        policyId={policyId}
                        draftId={draftId}
                        displayCheckSignatureButton={
                          user?.partner?.name === 'clearcover'
                        }
                      />
                    )}
                  </Column>
                )}
              </StaggeredAnimatedComponent>
              <div className={styles.buttonGroup}>
                <Modal
                  variant="normal"
                  toggle={openModal => (
                    <Button
                      data-testid="summary-exit"
                      variant="danger"
                      onClick={openModal}
                      disabled={isSubmitting}
                    >
                      Delete Draft
                    </Button>
                  )}
                >
                  {close => (
                    <ConfirmationModal
                      onSubmit={() => {
                        executeTerminateDraft().then(() => {
                          cleanupSignatureStorage();
                          redirectToPolicy({});
                        });
                      }}
                      close={close}
                      isSubmitting={terminateDraftloading}
                      error={terminateDraftError?.message}
                      headerText="Confirm discarding draft"
                      bodyText="Discarding draft will result in losing your current draft changes. Please confirm."
                    />
                  )}
                </Modal>
                <div className={styles.submitOrSave}>
                  <Button
                    data-testid="summary-save"
                    disabled={isSubmitting}
                    variant="secondary"
                    onClick={() => {
                      history.push(`/policies/${policyId}/draft/${draftId}`);
                    }}
                  >
                    Continue Editing
                  </Button>
                  <Button
                    data-testid="summary-submit"
                    variant="primary"
                    isSubmitting={isSubmitting}
                    onClick={() => handleSubmit()}
                    type="submit"
                    disabled={
                      !isValid ||
                      !packetsHaveBeenSigned ||
                      (requireAccessConsent && !accessConsentChecked)
                    }
                  >
                    Finalize Policy Changes
                  </Button>
                </div>
              </div>

              {/* Renewal Changes Warning Modal */}
              <WarningModal
                open={showRenewalChangesWarningModal}
                handleClose={() => {
                  setShowRenewalChangesWarningModal(false);
                }}
                title="By applying these changes, the upcoming renewal will also be updated."
                message="The changes you've made to your current policy will also apply to the upcoming renewal. Click “Apply Changes” to continue modifying both the current and renewal terms."
                buttons={[
                  {
                    testId: 'cancel-button',
                    variant: 'secondary',
                    size: 'md',
                    isSubmitting: false,
                    label: 'Cancel',
                    onClick: () => {
                      setSubmittingRef.current(false);
                    },
                    closeOnClick: true,
                  },
                  {
                    testId: 'confirm-button',
                    variant: 'primary',
                    size: 'md',
                    isSubmitting: false,
                    label: 'Apply Changes',
                    onClick: () => confirmAndSubmit(),
                    closeOnClick: true,
                  },
                ]}
              />

              {/* Renewal Error Warning Modal */}
              <WarningModal
                open={showRenewalErrorWarningModal}
                handleClose={() => {
                  setShowRenewalErrorWarningModal(false);
                  proceedWithRedirect();
                }}
                title="We were unable to apply these changes to the renewal term."
                message="While updating your upcoming renewal, an error occurred and the changes could not be applied. Please review the renewal term and adjust it as needed to ensure it reflects the desired state of the policy."
                buttons={[
                  {
                    testId: 'confirm-button',
                    variant: 'primary',
                    size: 'md',
                    isSubmitting: false,
                    label: 'Continue',
                    onClick: () => {},
                    closeOnClick: true,
                  },
                ]}
              />
            </Column>
          </div>
        )}
      </Formik>
    </PolicyContext.Provider>
  );
};
